import { computed } from 'vue'
// types
import type { ComputedRef } from 'vue'
import type { BlockReviewStoryProps } from '@ts-blocks/block-review-story/interfaces'
// hooks
import { useDefaultCtaUrl } from '@composables/tools/default-cta-url.hook'
import { useEventsSection } from '@hooks/events/sections/events-section.hook'

const CTA_FALLBACK_TEXT = 'View story details'

interface BlockReviewStoryHookOptions {
  block: () => BlockReviewStoryProps
}

interface BlockReviewStoryHookReturn {
  refImageAlt: ComputedRef<string>
  refShowTutor: ComputedRef<boolean>
  refShowCta: ComputedRef<boolean>
  refCtaText: ComputedRef<string>
  refCtaHref: ComputedRef<string>
  handleClickCta: () => void
}

export const useBlockReviewStory = ({ block }: BlockReviewStoryHookOptions): BlockReviewStoryHookReturn => {
  const refDefaultCtaUrl = useDefaultCtaUrl()
  const { buildCtaHref, trackSectionEvent } = useEventsSection({ sectionName: 'review-stories' })

  const refImageAlt = computed<string>(() => block().image?.alternativeText ?? '')

  const refShowTutor = computed<boolean>(() => block().showTutorName !== false && Boolean(block().tutorName))

  const refShowCta = computed<boolean>(() => block().showButton !== false)

  const refCtaText = computed<string>(() => block().button?.buttonText || CTA_FALLBACK_TEXT)

  const refCtaHref = computed<string>(() => {
    const button = block().button
    const base = (button?.isDefaultUrl ?? true) ? refDefaultCtaUrl.value : (button?.buttonLink ?? '#')
    return buildCtaHref(base, { text: refCtaText.value })
  })

  const handleClickCta = (): void => {
    trackSectionEvent('Button_click', { name: 'story_details', text: refCtaText.value, url: refCtaHref.value })
  }

  return {
    refImageAlt,
    refShowTutor,
    refShowCta,
    refCtaText,
    refCtaHref,
    handleClickCta,
  }
}
